import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../components/wrapper/API';
import { sortLocationsByName } from '../util/location';

/**
 * Locations for the event forms (create form, process popup, edit popup) and the admin page /locations.
 * `ensureLoaded()` fetches /Location/GetAll at most once per component instance; after a save in the
 * map dialog the caller merges the response via `upsert(loc)` instead of refetching.
 * Errors are silent (the select stays empty); a failed load may be retried by the next ensureLoaded().
 */
export default function useLocations() {
  const [locations, setLocations] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const requestedRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const ensureLoaded = useCallback(() => {
    if (requestedRef.current) return;
    requestedRef.current = true;
    setLoading(true);
    setFailed(false);
    api.get('/Location/GetAll')
      .then(res => {
        if (!mountedRef.current) return;
        setLocations(sortLocationsByName(Array.isArray(res.data) ? res.data : []));
        setLoaded(true);
      })
      .catch(() => {
        requestedRef.current = false;
        if (mountedRef.current) setFailed(true);
      })
      .finally(() => {
        if (mountedRef.current) setLoading(false);
      });
  }, []);

  const upsert = useCallback((location) => {
    if (!location || location.id == null) return;
    setLocations(prev => sortLocationsByName([...prev.filter(l => l.id !== location.id), location]));
  }, []);

  const remove = useCallback((id) => {
    setLocations(prev => prev.filter(l => l.id !== id));
  }, []);

  return { locations, loaded, loading, failed, ensureLoaded, upsert, remove };
}
